import { useEffect, useRef, useState } from "react";
import { Play, Pause, Rewind, FastForward } from "lucide-react";
import { getAudiobook } from "../lib/api";

/**
 * AudiobookRadio — a wooden-radio styled MP3 player card for the Story Barn.
 * • When an audiobook is uploaded (via Admin) it becomes playable.
 * • When nothing is uploaded, it renders in "Coming Soon" mode so the barn
 *   card still reads richly instead of being blank.
 *
 * Design: warm oak grain, honey-lit dial cutout, brass control ring, soft
 * VU-meter glow while playing. Matches the storybook aesthetic.
 */
function fmt(sec) {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function AudiobookRadio() {
  const audioRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const absUrl = meta?.url && !meta.url.startsWith("http") ? `${backendUrl}${meta.url}` : meta?.url;

  useEffect(() => {
    getAudiobook().then(setMeta).catch(() => setMeta({ available: false }));
  }, []);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const onTime = () => setCurrent(el.currentTime);
    const onLoad = () => setDuration(el.duration || 0);
    const onEnd = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onLoad);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onLoad);
      el.removeEventListener("ended", onEnd);
    };
  }, [meta?.url]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };
  const seekBy = (delta) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, Math.min((el.currentTime || 0) + delta, duration || 0));
  };
  const onScrub = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const pct = Number(e.target.value) / 100;
    el.currentTime = pct * duration;
    setCurrent(el.currentTime);
  };

  const available = !!meta?.available;

  return (
    <article
      data-testid="audiobook-radio"
      className="relative overflow-hidden rounded-2xl p-6 sm:p-8 border-2 border-[#3E2723]"
      style={{
        background:
          "repeating-linear-gradient(90deg, #4B2E1F 0px, #4B2E1F 1px, transparent 1px, transparent 8px), linear-gradient(180deg, #6B4A34 0%, #4B2E1F 100%)",
        boxShadow:
          "0 30px 60px -20px rgba(0,0,0,0.55), inset 0 0 0 4px rgba(255,255,255,0.06), inset 0 -18px 30px rgba(0,0,0,0.35)",
      }}
    >
      {/* brass corner rivets */}
      {[
        "top-3 left-3",
        "top-3 right-3",
        "bottom-3 left-3",
        "bottom-3 right-3",
      ].map((pos) => (
        <span
          key={pos}
          aria-hidden
          className={`absolute w-3 h-3 rounded-full ${pos}`}
          style={{
            background:
              "radial-gradient(circle at 30% 30%, #F4D492 0%, #C89A55 45%, #7A5225 100%)",
            boxShadow: "0 1px 2px rgba(0,0,0,0.6)",
          }}
        />
      ))}

      <div className="flex flex-col sm:flex-row items-stretch gap-5">
        {/* Honey speaker dial */}
        <div
          className="relative shrink-0 mx-auto sm:mx-0 w-32 h-32 sm:w-36 sm:h-36 rounded-full flex items-center justify-center"
          style={{
            background:
              "radial-gradient(circle at 50% 40%, #F5D084 0%, #C68A4A 45%, #7A4B24 90%)",
            boxShadow:
              "inset 0 4px 10px rgba(255,255,255,0.35), inset 0 -6px 16px rgba(0,0,0,0.45), 0 4px 10px rgba(0,0,0,0.35)",
          }}
        >
          {/* concentric grill lines */}
          <span
            aria-hidden
            className="absolute inset-3 rounded-full opacity-70"
            style={{
              background:
                "repeating-radial-gradient(circle at 50% 45%, rgba(60,32,12,0.5) 0 2px, transparent 2px 5px)",
            }}
          />
          <button
            type="button"
            data-testid="audiobook-play-btn"
            onClick={toggle}
            disabled={!available}
            aria-label={playing ? "Pause" : "Play"}
            className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center transition-transform duration-200 ${
              available ? "hover:scale-105 active:scale-95" : "opacity-60 cursor-not-allowed"
            }`}
            style={{
              background:
                "linear-gradient(180deg, #F5D084 0%, #A87149 100%)",
              boxShadow:
                "inset 0 2px 4px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.4), 0 3px 6px rgba(0,0,0,0.4)",
            }}
          >
            {playing ? (
              <Pause size={22} className="text-[#3E2723]" fill="#3E2723" />
            ) : (
              <Play size={22} className="text-[#3E2723] ml-0.5" fill="#3E2723" />
            )}
          </button>
        </div>

        {/* Marquee panel */}
        <div className="flex-grow min-w-0 flex flex-col justify-center">
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#F4D492]/80 mb-1">
            {available ? "Now playing" : "The Story Radio"}
          </p>
          <h3
            className="font-display text-2xl sm:text-3xl leading-tight text-[#F4D492]"
            style={{ textShadow: "0 1px 0 rgba(0,0,0,0.6)" }}
          >
            {meta?.title || "The Salty Moo Audiobook"}
          </h3>
          {meta?.subtitle && (
            <p className="font-script text-lg text-[#EED9AC] mt-1">{meta.subtitle}</p>
          )}

          {available ? (
            <div className="mt-4">
              <input
                data-testid="audiobook-scrubber"
                type="range"
                min={0}
                max={100}
                step="0.1"
                value={duration ? (current / duration) * 100 : 0}
                onChange={onScrub}
                className="w-full accent-[#F4D492]"
                aria-label="Seek"
              />
              <div className="flex items-center justify-between mt-2 text-xs text-[#F4D492]/85 font-body tabular-nums">
                <span data-testid="audiobook-current">{fmt(current)}</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    aria-label="Rewind 15 seconds"
                    onClick={() => seekBy(-15)}
                    className="p-1 rounded-full hover:bg-white/10 transition-colors"
                  >
                    <Rewind size={18} className="text-[#F4D492]" />
                  </button>
                  <button
                    type="button"
                    aria-label="Forward 15 seconds"
                    onClick={() => seekBy(15)}
                    className="p-1 rounded-full hover:bg-white/10 transition-colors"
                  >
                    <FastForward size={18} className="text-[#F4D492]" />
                  </button>
                </div>
                <span data-testid="audiobook-duration">{fmt(duration)}</span>
              </div>
              <audio
                ref={audioRef}
                data-testid="audiobook-audio-element"
                src={absUrl}
                preload="metadata"
              />
            </div>
          ) : (
            <p
              data-testid="audiobook-coming-soon"
              className="mt-3 text-sm text-[#EED9AC]/90 italic"
            >
              Cozy bedtime listening from Stefanie herself —{" "}
              <span className="coming-soon-flash text-[#F4D492] not-italic tracking-wide">
                coming soon
              </span>
              .
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
